/**
 * =============================================================================
 * Página: /afiliado/selecionar-conta
 * Versão: 1.0.0 (Plano v2 — Multi-Tenant para Afiliados)
 *
 * Descrição:
 *   Tela de seleção de conta exibida quando o mesmo usuário (auth.uid)
 *   possui contas de afiliado em mais de um tenant. Mostra um card por
 *   conta com nome do tenant, e-mail, número de programas e situação
 *   resumida. Ao clicar, chama `set_active_affiliate` (RPC) e redireciona
 *   para o painel.
 *
 * Acesso: rota AUTENTICADA (envolta no AffiliateAuthProvider, sem o guard
 * AffiliateProtectedRoute — o guard exigiria membership ativa).
 * =============================================================================
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Building2, ShieldCheck, ChevronRight } from 'lucide-react';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import {
  affiliateLoginPath,
  getLastAffiliateTenantSlug,
} from '@/lib/affiliate-urls';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export default function AffiliateSelectAccount() {
  const navigate = useNavigate();
  const { ready, user, availableAffiliates, switchAffiliate } =
    useAffiliateAuth();

  // ---------------------------------------------------------------------------
  // Sem usuário → manda para login
  // Apenas 1 conta → não há o que escolher, manda direto
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!ready) return;
    if (!user) {
      navigate(affiliateLoginPath(getLastAffiliateTenantSlug()), { replace: true });
      return;
    }
    if (availableAffiliates.length === 1) {
      navigate('/afiliado', { replace: true });
    }
  }, [ready, user, availableAffiliates, navigate]);

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (availableAffiliates.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center">
              Nenhuma conta de afiliado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <a href={affiliateLoginPath(getLastAffiliateTenantSlug())}>Voltar ao login</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Handler de seleção
  // ---------------------------------------------------------------------------
  const handleSelect = async (affiliateId: string) => {
    await switchAffiliate(affiliateId);
    navigate('/afiliado', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30 p-4 flex items-center justify-center">
      <Card className="w-full max-w-2xl shadow-xl border-border/50">
        <CardHeader className="space-y-2 text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <ShieldCheck className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">Escolha sua conta</CardTitle>
          <p className="text-sm text-muted-foreground">
            Você tem contas de afiliado em mais de uma organização. Selecione
            qual deseja acessar agora.
          </p>
        </CardHeader>

        <CardContent className="space-y-3">
          {availableAffiliates.map((acc) => {
            const hasActive = acc.memberships.some((m) => m.status === 'active');
            const hasPending = acc.memberships.some(
              (m) => m.status === 'pending',
            );
            return (
              <button
                key={acc.id}
                type="button"
                onClick={() => handleSelect(acc.id)}
                className="w-full text-left rounded-lg border border-border bg-card hover:bg-accent/40 transition-colors p-4 flex items-center gap-4 group"
              >
                <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{acc.full_name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {acc.email}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    <Badge variant="secondary" className="text-[10px]">
                      {acc.memberships.length}{' '}
                      {acc.memberships.length === 1 ? 'programa' : 'programas'}
                    </Badge>
                    {hasActive && (
                      <Badge className="text-[10px]">Ativo</Badge>
                    )}
                    {!hasActive && hasPending && (
                      <Badge variant="outline" className="text-[10px]">
                        Pendente
                      </Badge>
                    )}
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-transform" />
              </button>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
